import { useState, useRef, useEffect, useMemo } from 'react';
import { FaRegClock, FaTrophy, FaBookOpen, FaBell } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import { BrandedSpinner } from '@/components/BrandedSpinner';
import { useReadEvent, type MixedEventsPayload } from '@/hooks/useEvents';
import { useReadGlobalEvent } from '@/hooks/useGlobalEvents';
import { readAllEvents } from '@/hooks/useEvents';

interface NotificationBellProps {
  mixedEvents?: MixedEventsPayload;
  loading?: boolean;
  onEventClick?: (event: any) => void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ mixedEvents, loading, onEventClick }) => {
  const [open, setOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const markedRead = useRef<Set<string>>(new Set());
  const hasOpened = useRef(false); // Track if dropdown has ever been opened
  const { mutate: markEventRead } = useReadEvent();
  const { mutate: markGlobalEventRead } = useReadGlobalEvent();
  const { mutate: markAllEventsRead } = readAllEvents();

  const typeIconMap: Record<string, { Icon: IconType; bgClass: string; iconClass: string }> = {
    LEVEL_UP: { Icon: FaTrophy, bgClass: 'bg-amber-50 ring-amber-200', iconClass: 'text-amber-600' },
    NEW_CONTENT: { Icon: FaBookOpen, bgClass: 'bg-blue-50 ring-blue-200', iconClass: 'text-blue-600' },
    NOTIFICATION: { Icon: FaBell, bgClass: 'bg-violet-50 ring-violet-200', iconClass: 'text-violet-600' },
    POINT_REWARD: { Icon: FaTrophy, bgClass: 'bg-amber-50 ring-amber-200', iconClass: 'text-amber-600' },
  };

  const getIconForType = (type?: string) =>
    typeIconMap[type || ''] || { Icon: FaBell, bgClass: 'bg-gray-50 ring-gray-200', iconClass: 'text-gray-500' };

  // Function to calculate time ago
  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const eventDate = new Date(dateString);
    const diffInMilliseconds = now.getTime() - eventDate.getTime();
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) {
      return 'just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    } else {
      return eventDate.toLocaleDateString();
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close dropdown on Escape and return focus to the bell button
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        bellRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  type CombinedEvent = {
    id: string;
    name: string;
    description: string;
    type?: string;
    link?: string | null;
    createdAt: string;
    read: boolean;
    isGlobal: boolean;
  };

  const sortedEvents: CombinedEvent[] = useMemo(() => {
    const user = (mixedEvents?.userEvents ?? []).map(e => ({
      id: e.id,
      name: e.name,
      description: e.description,
      type: e.type,
      link: e.link ?? undefined,
      createdAt: e.createdAt,
      read: !!e.read,
      isGlobal: false,
    } as CombinedEvent));

    const global = (mixedEvents?.globalEvents ?? []).map(ge => ({
      id: ge.id,
      name: ge.name,
      description: ge.description,
      type: ge.type,
      link: ge.link ?? undefined,
      createdAt: ge.createdAt,
      read: !!((ge as any).isRead ?? (Array.isArray((ge as any).reads) && (ge as any).reads.length > 0)),
      isGlobal: true,
    } as CombinedEvent));

    return [...user, ...global].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [mixedEvents]);
  const unreadEvents = sortedEvents.filter(e => !e.read);
  const readEvents = sortedEvents.filter(e => e.read);

  const handleMarkRead = (event: CombinedEvent) => {
    const key = `${event.isGlobal ? 'g' : 'u'}:${event.id}`;
    if (event.read || markedRead.current.has(key)) return;
    markedRead.current.add(key);
    if (event.isGlobal) {
      markGlobalEventRead(event.id);
    } else {
      markEventRead(event.id);
    }
  };

  // Mark all notifications as read when dropdown closes after being opened at least once
  useEffect(() => {
    if (open) {
      hasOpened.current = true;
    } else if (hasOpened.current) {
      markAllEventsRead();
    }
  }, [open]);

  return (
    <div className="relative z-[70]">
      <button
        ref={bellRef}
        type="button"
        aria-label={unreadEvents.length > 0 ? `Notifications, ${unreadEvents.length} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 text-slate-600 hover:text-slate-800 cursor-pointer rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
      >
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {unreadEvents.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-[10px] font-semibold text-white leading-none">
              {unreadEvents.length > 99 ? '99+' : unreadEvents.length}
            </span>
          </span>
        )}
      </button>
      {open && (
        <div
          ref={dropdownRef}
          aria-hidden={!open}
          className="fixed inset-x-0 top-[68px] w-screen bg-white border-t border-b border-gray-200 rounded-none shadow-lg z-[80] overflow-hidden
          sm:absolute sm:top-auto sm:right-0 sm:left-auto sm:mt-2 sm:w-[24rem] sm:max-w-[calc(100vw-1rem)] sm:border sm:rounded-xl"
        >
          <div className="p-4 pl-6 border-b border-gray-100 text-lg font-medium text-gray-900 bg-white flex items-center justify-between gap-2">
            <span>Notifications</span>
            {/* {sortedEvents.length > 0 && (
              <Button
                variant="ghost"
                size="small"
                className="text-gray-600 hover:text-gray-900 underline"
                onClick={() => markAllEventsRead()}
                aria-label="Clear notifications"
              >
                Clear
              </Button>
            )} */}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading && sortedEvents.length === 0 ? (
              <div className="flex justify-center items-center py-8">
                <BrandedSpinner size="md" />
              </div>
            ) : sortedEvents.length === 0 ? (
              <div className="text-center text-gray-500 py-8">No notifications</div>
            ) : (
              <ul>
                {unreadEvents.map(event => {
                  const { Icon, bgClass, iconClass } = getIconForType(event.type);
                  return (
                    <li key={event.id} className="mx-2">
                    <button
                      type="button"
                      className={`group flex w-full flex-col text-left px-2 py-3 border-b border-gray-100 cursor-pointer border-b-1 hover:border-l-1 hover:border-t-1 hover:border-r-1 hover:border-b-1 hover:border-violet-300 hover:bg-violet-50 rounded-lg transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 ${event.read ? 'opacity-99 text-gray-900' : ''}`}
                      onClick={() => { handleMarkRead(event); onEventClick?.(event); }}
                      onMouseEnter={() => handleMarkRead(event)}
                      onFocus={() => handleMarkRead(event)}
                    >
                      <div className="flex w-full items-center gap-3">
                        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full ring-1 flex-none ${bgClass}`}>
                          <Icon className={`w-4.5 h-4.5 ${iconClass}`} />
                        </span>
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="font-semibold text-gray-900 group-hover:text-gray-800 transition-colors">{event.name}</div>
                            <div className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
                              {event.read && (
                                <span className="px-2 py-0.5 rounded-full  bg-gray-50">Seen</span>
                              )}
                            </div>
                          </div>
                          <div className="text-sm text-gray-700 line-clamp-4 group-hover:text-gray-600 whitespace-pre-line w-full">{event.description}</div>
                          <div className="flex items-center gap-1 mt-1">
                            <FaRegClock className="text-xs text-gray-400" />
                            <span className="text-xs  text-gray-400">{getTimeAgo(event.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                    </li>
                  );
                })}
                {readEvents.length > 0 && (
                  <li className="px-4 py-2 text-sm font-light text-gray-500 bg-gray-50 border-y border-gray-100 select-none flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="gray">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Seen
                  </li>
                )}
                {readEvents.map(event => {
                  const { Icon, bgClass, iconClass } = getIconForType(event.type);
                  return (
                    <li key={event.id} className="mx-2">
                    <button
                      type="button"
                      className={`group flex w-full flex-col text-left px-2 py-3 border-b-1 border-gray-100 cursor-pointer border-1 border-l-white border-r-white border-t-white border-b-gray-100 br-0 hover:border-l-1 hover:border-t-1 hover:border-r-1 hover:border-b-1 hover:border-violet-300 hover:bg-violet-50 rounded-lg transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 ${event.read ? 'opacity-99 text-gray-900' : ''}`}
                      onClick={() => { handleMarkRead(event); onEventClick?.(event); }}
                      onMouseEnter={() => handleMarkRead(event)}
                      onFocus={() => handleMarkRead(event)}
                    >
                      <div className="flex w-full items-center gap-3">
                        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full ring-1 flex-none ${bgClass}`}>
                          <Icon className={`w-4.5 h-4.5 ${iconClass}`} />
                        </span>
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="font-semibold text-gray-900 group-hover:text-gray-800 transition-colors leading-tight">{event.name}</div>
                            <div className="flex items-center gap-1 text-xs text-gray-400 shrink-0">
                              {event.read && (
                                <span className="px-2 py-0.5 rounded-full  bg-gray-50">Seen</span>
                              )}
                            </div>
                          </div>
                          <div className="text-sm text-gray-700 line-clamp-4 group-hover:text-gray-600 whitespace-pre-line w-full">{event.description}</div>
                          <div className="flex items-center gap-1 mt-1">
                            <FaRegClock className="text-xs text-gray-400" />
                            <span className="text-xs  text-gray-400">{getTimeAgo(event.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell; 